
export type PermissionKey = "APP_PERMISSION" | "READ_WAREHOUSE" | "WRITE_WAREHOUSE" | "READ_SUBSCRIPTION" | "WRITE_SUBSCRIPTION" | "READ_THREADS" | "WRITE_THREADS" | "READ_USERMANAGEMENT" | "WRITE_USERMANAGEMENT" | "READ_GODVIEW" | "READ_ADMINFEEDBACKLIST" | "READ_ADOPTION_METRICS" | "READ_INTEGRATIONS" | "WRITE_INTEGRATIONS" | "READ_DASHBOARD" | "WRITE_DASHBOARD" | "READ_USECASE" | "WRITE_USECASE" | "SHARE_ANY_COMPANY" | "SHARE_OWN_ORG" | "READ_CONTEXT_CONSOLE" | "READ_DASHBOARD_PROMPTS" | "VIEW_INTEGRATION_STATUS" | "MANAGE_INTEGRATIONS";
    
export const FEATURE_PERMISSION_MAPPING: Record<PermissionKey, string | string[]> = {
    APP_PERMISSION: ["iq:company:user:read", "iq:user:user:read"], //TO-DO
    READ_WAREHOUSE: "iq:company:warehouse:read",
    WRITE_WAREHOUSE: "iq:company:warehouse:write",
    READ_SUBSCRIPTION: "iq:company:subscription:read",
    WRITE_SUBSCRIPTION: "iq:company:subscription:write",
    READ_THREADS: "iq:user:threads:read",
    WRITE_THREADS: "iq:user:threads:write",
    READ_USERMANAGEMENT: "iq:company:user:read",
    WRITE_USERMANAGEMENT: "iq:company:user:write",
    READ_GODVIEW: "iq:godview:company:read",
    READ_ADMINFEEDBACKLIST: "iq:adminfeedbacklist:company:read",
    READ_ADOPTION_METRICS: "iq:adoptionmetrics:company:read",
    // Any-of across company / user / admin, matching Slack `require_permissions`
    // (status: read; install-url / share-install-url / disconnect /
    // /slack/apps/{app_key}/*: write). IQUser holds `iq:user:integration:*`.
    // Settings is visible to all users; Slack write actions use WRITE_INTEGRATIONS
    // so they appear only when the corresponding backend routes will authorize them.
    // READ_INTEGRATIONS is intentionally unused — the Integrations page is open
    // to everyone; keep the mapping so JWT decoding stays aligned with backend.
    READ_INTEGRATIONS: [
      "iq:company:integration:read",
      "iq:user:integration:read",
      "iq:admin:integration:read",
    ],
    WRITE_INTEGRATIONS: [
      "iq:company:integration:write",
      "iq:user:integration:write",
      "iq:admin:integration:write",
    ],
    // Dashboard / use-case capabilities (iq-backend PRs #896/#897, modified by #914).
    // Any-of across the company/user/admin tiers — the server decides which tier a
    // role carries. The write trio still gates the creation routes backend-side.
    READ_DASHBOARD: ["iq:company:dashboard:read", "iq:user:dashboard:read", "iq:admin:dashboard:read"],
    WRITE_DASHBOARD: ["iq:company:dashboard:write", "iq:user:dashboard:write", "iq:admin:dashboard:write"],
    READ_USECASE: ["iq:company:usecase:read", "iq:user:usecase:read", "iq:admin:usecase:read"],
    WRITE_USECASE: ["iq:company:usecase:write", "iq:user:usecase:write", "iq:admin:usecase:write"],
    // Share personas. Sharing is a dedicated backend action (iq-backend #914) — the
    // `dashboard:share` strings, not the write trio: admin tier can pick any company;
    // company tier shares within its own org. There is no user-tier share.
    SHARE_ANY_COMPANY: ["iq:admin:dashboard:share"],
    SHARE_OWN_ORG: ["iq:company:dashboard:share"],
    // Context Console (self-serve context upload/evaluate/publish). Any-of across
    // both tiers, mirroring the backend's require_permissions gate: IQAdmin holds
    // the `iq:company:*` string, SarasAdmin/IQDashboardAdmin hold `iq:admin:*`
    // (both provisioned by create_all_iq_roles_and_permissions_v8.sql).
    //
    // Deliberately absent from SARAS_ROLES_MAPPING below: that map grants by ROLE
    // NAME without checking any held permission, so listing it there would open
    // this UI for an admin token even where v8 is not applied and the backend
    // still 403s the upload. Checking the HELD permission keeps UI and backend
    // in lockstep per environment.
    READ_CONTEXT_CONSOLE: ["iq:company:contextconsole:write", "iq:admin:contextconsole:write"],
    // Published-dashboard creation conversation (read-only "Conversation" sheet).
    // Exact admin-tier write only, mirroring the backend gate on
    // `GET /dashboards/admin/{id}/conversation` — company/user write, admin read,
    // ownership, or a share do NOT unlock it. Editing will use the same key.
    //
    // Deliberately absent from SARAS_ROLES_MAPPING for the same reason as
    // READ_CONTEXT_CONSOLE: a role name alone must not open this UI; only the
    // HELD permission does, so UI and backend stay in lockstep.
    READ_DASHBOARD_PROMPTS: ["iq:admin:dashboard:write"],
    // Company connection gates, mirroring the backend `require_permissions`:
    // VIEW_INTEGRATION_STATUS opens the connection read (GET /teams/status needs
    // company or admin read); MANAGE_INTEGRATIONS opens claim and disconnect
    // (company or admin write). The user tier (IQUser) is left out on purpose
    // because the backend 403s it, so READ/WRITE_INTEGRATIONS cannot be reused.
    //
    // Both deliberately absent from SARAS_ROLES_MAPPING for the same reason as
    // READ_CONTEXT_CONSOLE: only the HELD permission opens this UI, so UI and
    // backend stay in lockstep.
    VIEW_INTEGRATION_STATUS: ["iq:company:integration:read", "iq:admin:integration:read"],
    MANAGE_INTEGRATIONS: ["iq:company:integration:write", "iq:admin:integration:write"],
}

export const SARAS_ROLES_MAPPING: Partial<Record<PermissionKey, string | string[]>> = {
    APP_PERMISSION: ["iq:company:user:read", "iq:user:user:read"], //TO-DO
    READ_GODVIEW: "iq:godview:company:read",
    READ_ADMINFEEDBACKLIST: "iq:adminfeedbacklist:company:read",
    READ_ADOPTION_METRICS: "iq:adoptionmetrics:company:read",
    // Only the KEYS matter here — this map is the blanket elevation set applied to
    // ADMIN_ROLES in `hasAppPermision`; values mirror FEATURE_PERMISSION_MAPPING
    // for consistency.
    READ_DASHBOARD: ["iq:company:dashboard:read", "iq:user:dashboard:read", "iq:admin:dashboard:read"],
    WRITE_DASHBOARD: ["iq:company:dashboard:write", "iq:user:dashboard:write", "iq:admin:dashboard:write"],
    READ_USECASE: ["iq:company:usecase:read", "iq:user:usecase:read", "iq:admin:usecase:read"],
    WRITE_USECASE: ["iq:company:usecase:write", "iq:user:usecase:write", "iq:admin:usecase:write"],
    SHARE_ANY_COMPANY: ["iq:admin:dashboard:share"],
    SHARE_OWN_ORG: ["iq:company:dashboard:share"],
}

export const errorCodes = {
    USER_EXISTS: "US406"
}
